import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_USER_CLIENTS_LIST_QUERY_KEY } from '@/modules/common/queries/clients.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CURRENT_REPORTS_BATCH_QUERY_KEY,
  REPORTS_BATCH_LIST_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/reports-batch.ts';
import {
  CreateReportRequest,
  CreateReportResponse,
  GetReportListResponse,
  GetReportListV1ReportsGetParams,
} from 'compliance-api';
import { ReportResponse, ReportStatus } from 'compliance-api';

export const REPORTS_LIST_QUERY_KEY = 'compliance-common-reports-list';

export const useReportsListQuery = (
  payload: GetReportListV1ReportsGetParams,
  disabled = false,
  options?: { enableGeneratingPolling?: boolean },
) => {
  const businessId = useGetCurrentBusinessId();
  return useInfiniteQuery<GetReportListResponse>({
    queryKey: [REPORTS_LIST_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 999 * 60 * 60, // 1 hour
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
    notifyOnChangeProps: ['data', 'hasNextPage'],
    refetchInterval: data => {
      if (!options?.enableGeneratingPolling) return 999 * 60 * 60;
      const reports = (data?.pages || []).flatMap(page => page?.data || []);
      if (!reports.length) return false;
      const hasGenerating = reports.some(report => report.status === ReportStatus.Generating);
      return hasGenerating ? 5000 : false;
    },
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getReportListV1ReportsGet({
        ...payload,
        page: pageParam || payload.page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useCreateReportMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: CreateReportResponse) => Promise<void> | void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreateReportRequest) => {
      const response = await withErrorHandling(api.v1.createReportV1ReportsPost(payload), {
        loading: 'Creating report...',
        success: 'Generating report...',
        setErrors,
      });
      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries([REPORTS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
      onSuccess && (await onSuccess(data));
    },
  });
};

export const useDeleteReportMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(api.v1.removeReportV1ReportsIdDelete({ id }), {
        loading: 'Archiving report...',
        success: 'Report archived',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([REPORTS_BATCH_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_REPORTS_BATCH_QUERY_KEY]);
    },
  });
};

export const useRestoreReportMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(api.v1.removeReportV1ReportsIdDelete({ id }), {
        loading: 'Restoring report...',
        success: 'Report restored',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([REPORTS_BATCH_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_REPORTS_BATCH_QUERY_KEY]);
    },
  });
};

/**
 * Polls report list by filters and invokes callbacks when the specific report reaches a terminal status.
 */
export const usePollReportUntilReady = (
  payload: (GetReportListV1ReportsGetParams & { id?: number }) | null,
  options?: {
    enabled?: boolean;
    intervalMs?: number;
    onCompleted?: (report: ReportResponse) => void;
    onFailed?: (report: ReportResponse) => void;
  },
) => {
  const enabled = !!payload && (options?.enabled ?? true);
  const interval = enabled ? options?.intervalMs ?? 5000 : false;

  const query = useQuery({
    queryKey: [REPORTS_LIST_QUERY_KEY, JSON.stringify(payload)],
    enabled,
    staleTime: 999 * 60 * 60, // 1 hour
    refetchInterval: interval,
    queryFn: async () => {
      if (!payload) return null;
      const response = await api.v1.getReportListV1ReportsGet({
        page: payload?.page,
        page_size: payload?.page_size,
        filter_by_name: payload?.filter_by_name,
        filter_by_type: payload?.filter_by_type,
        filter_by_format: payload?.filter_by_format,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    select: data => {
      if (!data || !payload) return null;
      return (data.data || []).find(r => r.id === payload?.id);
    },
    onSuccess: data => {
      if (data?.status === ReportStatus.Completed) {
        options?.onCompleted && options.onCompleted(data);
      }
      if (data?.status === ReportStatus.Failed) {
        options?.onFailed && options.onFailed(data);
      }
    },
  });

  return query;
};
